import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BannerComponent } from '../../../../../shared/components/banner/banner.component';
import { environment } from '../../../../../../environments';
import {
  LucideAngularModule,
  IdCard,
  ClipboardList,
  Files,
  History,
  X as CloseIcon,
  Menu,
  Download,
} from 'lucide-angular';
import { AdministrativeDataComponent } from '../../../components/administrative-data';
import { MedicalRecordComponent } from '../../../components/medical-record';
import { DocumentsComponent } from '../../../components/documents';
import {
  MedicalHistoryComponent,
  Beneficiary as MedicalHistoryBeneficiary,
} from '../../../components/medical-history';
import { ContactsAndFiliationComponent } from '../../../components/contacts-and-filiation';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { AppState } from '../../../../../store/app.state';
import { selectAllSubAccounts } from '../../../../../store/sub-accounts/sub-accounts.selectors';
import { selectUserData } from '../../../../../store/auth/auth.selectors';
import { Country } from '../../../../../core/interfaces/doctor.interface';
import { AuthorizationService } from '../../../../../core/services/authorization.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { PatientRecordExportService } from '../../../../../core/services/patient-record-export.service';

@Component({
  selector: 'app-patient-record-overview',
  standalone: true,
  imports: [
    CommonModule,
    BannerComponent,
    LucideAngularModule,
    AdministrativeDataComponent,
    MedicalRecordComponent,
    DocumentsComponent,
    MedicalHistoryComponent,
    ContactsAndFiliationComponent,
  ],
  templateUrl: './overview.component.html',
})
export class PatientRecordOverviewComponent implements OnInit {
  @ViewChild('menuRoot', { read: ElementRef })
  menuRoot?: ElementRef<HTMLElement>;

  readonly Menu = Menu;
  readonly CloseIcon = CloseIcon;
  readonly DownloadIcon = Download;
  isDppTheme = environment.theme.default === 'dpp';
  selectedCountry: Country | null = null;
  links = [
    { label: 'Votre passeport', href: '/passport' },
    { label: 'Votre dossier patient', href: undefined },
  ];
  medicalRecordMenu = [
    {
      label: 'Données administratives',
      key: 'administrative_data',
      icon: IdCard,
    },
    {
      label: 'Interrogatoire Médical',
      key: 'medical_record',
      icon: ClipboardList,
    },
    { label: 'Documents', key: 'documents', icon: Files },
    { label: 'Historique de soins', key: 'medical_history', icon: History },
  ];
  selectedMenu = this.medicalRecordMenu[0];
  isMenuOpen = false;
  // Sous-comptes pour le composant documents
  subAccounts: Array<{ fullname: string; passportNumber: string }> = [];

  // Bénéficiaires pour le composant Documents
  documentsBeneficiaries: MedicalHistoryBeneficiary[] = [];

  // Bénéficiaires pour le composant MedicalHistory (prestations)
  medicalHistoryBeneficiaries: MedicalHistoryBeneficiary[] = [];

  /** Indique qu'un téléchargement (export PDF) est en cours */
  exportLoading = false;

  /** Type d’onglet Interrogatoire médical (comorbidités / antécédents), pour l’export */
  constructor(
    private store: Store<AppState>,
    private authorizationService: AuthorizationService,
    private authService: AuthService,
    private patientRecordExportService: PatientRecordExportService,
  ) {}

  ngOnInit(): void {
    // Récupérer le pays sélectionné depuis le localStorage
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          this.selectedCountry = JSON.parse(saved);
          // Mettre à jour le lien du passeport avec le code du pays
          this.updatePassportLink();
        } catch {
          this.selectedCountry = null;
        }
      }
    }

    // Récupérer les sous-comptes pour le composant documents
    this.store.select(selectAllSubAccounts).subscribe((subAccounts) => {
      this.subAccounts = subAccounts.map((account: any) => ({
        fullname: `${account.firstName} ${account.lastName}`,
        passportNumber: account.passportNumber,
      }));
    });

    // Récupérer les bénéficiaires pour les documents et l'historique médical
    this.fetchDocumentsBeneficiaries();
    this.fetchMedicalHistoryBeneficiaries();
  }

  private updatePassportLink(): void {
    if (this.selectedCountry?.code) {
      this.links[0].href = `/${this.selectedCountry.code}/passport`;
    }
  }

  getAge(birthDate: string): number {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isMenuOpen) {
      return;
    }
    const root = this.menuRoot?.nativeElement;
    const target = event.target as Node | null;
    if (!root || !target || root.contains(target)) {
      return;
    }
    this.isMenuOpen = false;
  }

  fetchDocumentsBeneficiaries(): void {
    this.authorizationService.getAccessibleUsers().subscribe({
      next: (response) => {
        // Filtrer les utilisateurs pour les documents
        const filteredUsers = response.users.filter((user: any) => {
          // Critère 1: Si c'est l'utilisateur courant, on le prend d'office
          if (user.isCurrentUser === true) {
            return true;
          }

          // Critère 2: Sinon, on ne prend que les sous-comptes
          if (user.isSubAccount !== true) {
            return false;
          }

          // Critère 3: Vérifier les autorisations pour documents
          return user.authorizations.some((auth: any) => {
            // Vérifier si le statut est 'active'
            if (auth.status !== 'active') {
              return false;
            }

            // Vérifier s'il y a des permissions wildcard (accès total)
            const hasWildcardPermission = auth.permissions.some(
              (perm: any) =>
                perm.resourceType === '*' &&
                perm.actions.includes('*') &&
                (perm.scope === 'all' || !perm.scope),
            );

            if (hasWildcardPermission) {
              return true;
            }

            // Vérifier s'il y a une permission pour documents (tout scope: medical, financial, ou aucun)
            const hasDocumentsPermission = auth.permissions.some(
              (perm: any) =>
                perm.resourceType === 'documents' &&
                perm.actions.includes('read'),
            );

            return hasDocumentsPermission;
          });
        });

        // Mapper les utilisateurs filtrés vers le format MedicalHistoryBeneficiary
        this.documentsBeneficiaries = filteredUsers.map((user: any) => ({
          fullname: `${user.firstName} ${user.lastName}`,
          passportNumber: user.passportNumber,
        }));

        console.log(
          'Bénéficiaires filtrés pour Documents (overview):',
          this.documentsBeneficiaries,
        );
      },
      error: (err) => {
        console.error(
          'Erreur lors du chargement des utilisateurs accessibles pour documents:',
          err,
        );
      },
    });
  }

  fetchMedicalHistoryBeneficiaries(): void {
    this.authorizationService.getAccessibleUsers().subscribe({
      next: (response) => {
        // Filtrer les utilisateurs pour l'historique médical (prestations)
        const filteredUsers = response.users.filter((user: any) => {
          // Critère 1: Si c'est l'utilisateur courant, on le prend d'office
          if (user.isCurrentUser === true) {
            return true;
          }

          // Critère 2: Sinon, on ne prend que les sous-comptes
          if (user.isSubAccount !== true) {
            return false;
          }

          // Critère 3: Vérifier les autorisations pour prestations/medical_record
          return user.authorizations.some((auth: any) => {
            // Vérifier si le statut est 'active'
            if (auth.status !== 'active') {
              return false;
            }

            // Vérifier s'il y a des permissions wildcard (accès total)
            const hasWildcardPermission = auth.permissions.some(
              (perm: any) =>
                perm.resourceType === '*' &&
                perm.actions.includes('*') &&
                (perm.scope === 'all' || !perm.scope),
            );

            if (hasWildcardPermission) {
              return true;
            }

            // Vérifier s'il y a une permission pour medical_record ou prestations (tout scope)
            const hasMedicalPermission = auth.permissions.some(
              (perm: any) =>
                (perm.resourceType === 'medical_record' ||
                  perm.resourceType === 'prestations') &&
                perm.actions.includes('read'),
            );

            return hasMedicalPermission;
          });
        });

        // Mapper les utilisateurs filtrés vers le format MedicalHistoryBeneficiary
        this.medicalHistoryBeneficiaries = filteredUsers.map((user: any) => ({
          fullname: `${user.firstName} ${user.lastName}`,
          passportNumber: user.passportNumber,
        }));

        console.log(
          'Bénéficiaires filtrés pour MedicalHistory (overview):',
          this.medicalHistoryBeneficiaries,
        );
      },
      error: (err) => {
        console.error(
          'Erreur lors du chargement des utilisateurs accessibles pour prestations:',
          err,
        );
      },
    });
  }

  /**
   * Télécharger / Imprimer le dossier patient pour la section donnée.
   * - administrative_data : export identité patient via GET /medical-records/export-patient-identity
   * - medical_record : export dossier médical via GET /medical-records/export-medical-record
   */
  onDownloadPrint(section: string): void {
    console.log('onDownloadPrint', section);
    if (section === 'administrative_data') {
      this.exportLoading = true;
      this.store
        .select(selectUserData)
        .pipe(take(1))
        .subscribe((userData) => {
          const passportNumber = userData?.passportNumber;
          if (!passportNumber) {
            this.exportLoading = false;
            return;
          }
          this.patientRecordExportService
            .exportPatientIdentityPdf(passportNumber)
            .subscribe({
              next: (blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `dossier-patient-identite-${Date.now()}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                this.exportLoading = false;
              },
              error: (err) => {
                console.error('Erreur export identité patient:', err);
                this.exportLoading = false;
              },
            });
        });
    }
    if (section === 'medical_record') {
      this.exportLoading = true;
      this.store
        .select(selectUserData)
        .pipe(take(1))
        .subscribe((userData) => {
          const passportNumber = userData?.passportNumber;
          if (!passportNumber) {
            this.exportLoading = false;
            return;
          }
          this.patientRecordExportService
            .exportMedicalRecordPdf(passportNumber)
            .subscribe({
              next: (blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `dossier-patient-medical-${Date.now()}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                this.exportLoading = false;
              },
              error: (err) => {
                console.error('Erreur export dossier médical:', err);
                this.exportLoading = false;
              },
            });
        });
    }
  }
}
